import { STOREFRONTS, localizationLanguagesForStorefront } from '../data/storefronts.js';

export const OPPORTUNITY_SOURCE_MARKETS = Object.freeze([
  'us', 'gb', 'ca', 'au', 'jp', 'kr', 'cn', 'tw',
  'hk', 'de', 'fr', 'it', 'es', 'br', 'mx', 'in',
]);

export const OPPORTUNITY_TARGET_MARKETS_PRIMARY = Object.freeze(['us', 'cn', 'au', 'jp', 'kr']);
export const OPPORTUNITY_TARGET_MARKETS_SECONDARY = Object.freeze([]);
export const OPPORTUNITY_TARGET_MARKETS = Object.freeze([
  ...OPPORTUNITY_TARGET_MARKETS_PRIMARY,
  ...OPPORTUNITY_TARGET_MARKETS_SECONDARY,
]);

export const OPPORTUNITY_RADAR_WEIGHTS = Object.freeze({
  sourceHeat: 0.3,
  trendVelocity: 0.2,
  targetCold: 0.25,
  localizationGap: 0.15,
  substituteWeakness: 0.1,
});

export const OPPORTUNITY_RADAR_PARAMS = Object.freeze({
  chartDepth: 100,
  topSourceRank: 50,
  weakTargetRank: 75,
  lowTargetRatingCount: 100,
  strongSubstituteRatingCount: 1_000,
  persistenceReferenceDays: 7,
  velocityRankGainReference: 50,
  substituteTopLimit: 30,
  strongSubstituteReferenceCount: 3,
});

/**
 * @typedef {object} OpportunityCandidateApp
 * @property {number|string} trackId - Apple app id from iTunes Lookup or RSS.
 * @property {string} name - App name from public Apple metadata.
 * @property {string|null} genreId - Apple primary genre id.
 * @property {string|null} genreName - Apple primary genre name.
 */

/**
 * @typedef {object} OpportunityMarketSignal
 * @property {string} market - Two-letter storefront code.
 * @property {boolean} [available=true] - False when lookup returns no app for the storefront.
 * @property {number|null} [chartRank] - Best published App Store chart rank for this app.
 * @property {number|null} [previousChartRank] - Previous scan rank; null when it was absent.
 * @property {boolean} [isNewEntry] - True when this app newly entered a source chart.
 * @property {number|null} [ratingCount] - `userRatingCount` from iTunes Lookup.
 * @property {number|null} [averageRating] - `averageUserRating` from iTunes Lookup.
 * @property {string[]} [languageCodesISO2A] - `languageCodesISO2A` from iTunes Lookup.
 * @property {number|null} [seenDays] - Consecutive days observed in source charts.
 */

/**
 * @typedef {object} CategorySubstitute
 * @property {string} market - Target storefront code.
 * @property {string|null} genreId - Apple genre id used for the target chart.
 * @property {number} [rankLimit=30] - Depth of the category chart sample.
 * @property {boolean} [feedOk=true] - False when chart data is unavailable.
 * @property {Array<{trackId: number|string, rank: number, genreId?: string|null, ratingCount?: number|null}>} entries
 */

/**
 * @typedef {object} OpportunityTargetScore
 * @property {string} market
 * @property {'primary'|'secondary'} targetTier
 * @property {number|null} score
 * @property {{sourceHeat: number|null, trendVelocity: number|null, targetCold: number|null, localizationGap: number|null, substituteWeakness: number|null}} factors
 * @property {{code: string, signal: keyof typeof OPPORTUNITY_RADAR_WEIGHTS, detail: string}[]} reasons
 */

/**
 * @typedef {object} OpportunityCandidate
 * @property {OpportunityCandidateApp} app
 * @property {OpportunityMarketSignal[]} signals
 * @property {CategorySubstitute[]} [substitutes]
 */

/**
 * Score one Opportunity Radar candidate across the configured target markets.
 * Pure data shaping only: no network, no storage, no accounts.
 *
 * @param {OpportunityCandidate} candidate
 * @param {{sourceMarkets?: readonly string[], targetMarkets?: readonly string[], secondaryTargetMarkets?: readonly string[]}} [options]
 * @returns {{app: OpportunityCandidateApp|null, score: number|null, targetScores: OpportunityTargetScore[], sourceSummary: object, topReasons: OpportunityTargetScore['reasons']}}
 */
export function scoreOpportunityCandidate(candidate, options = {}) {
  const app = candidate && typeof candidate === 'object' ? candidate.app ?? null : null;
  const signals = Array.isArray(candidate?.signals) ? candidate.signals : [];
  const substitutes = Array.isArray(candidate?.substitutes) ? candidate.substitutes : [];

  const sourceMarkets = normalizeMarketSet(options.sourceMarkets ?? OPPORTUNITY_SOURCE_MARKETS);
  const targetMarkets = normalizeMarketList(options.targetMarkets ?? OPPORTUNITY_TARGET_MARKETS);
  const secondaryTargets = normalizeMarketSet(options.secondaryTargetMarkets ?? OPPORTUNITY_TARGET_MARKETS_SECONDARY);

  const sourceSignals = signals.filter((signal) => sourceMarkets.has(normalizeMarket(signal.market)));
  const sourceSummary = summarizeSourceMarkets(sourceSignals, sourceMarkets.size);
  const signalByMarket = new Map(signals.map((signal) => [normalizeMarket(signal.market), signal]));
  const substituteByMarket = new Map(substitutes.map((substitute) => [normalizeMarket(substitute.market), substitute]));
  const sourceRatingReference = sourceRatingMedian(sourceSignals);

  const targetScores = targetMarkets.map((market) => {
    const signal = signalByMarket.get(market) ?? { market, signalMissing: true };
    const substitute = substituteByMarket.get(market) ?? null;
    return scoreTargetMarket({
      market,
      targetTier: secondaryTargets.has(market) ? 'secondary' : 'primary',
      signal,
      substitute,
      sourceSummary,
      sourceRatingReference,
    });
  });

  const scoredTargets = targetScores.filter((target) => target.score !== null);
  const score = scoredTargets.length === 0
    ? null
    : Math.round(scoredTargets.reduce((sum, target) => sum + target.score, 0) / scoredTargets.length);

  return {
    app,
    score,
    targetScores,
    sourceSummary,
    topReasons: topReasons(scoredTargets),
  };
}

/**
 * @param {Parameters<typeof scoreTargetMarket>[0]} input
 * @returns {OpportunityTargetScore}
 */
function scoreTargetMarket({ market, targetTier, signal, substitute, sourceSummary, sourceRatingReference }) {
  const factors = {
    sourceHeat: sourceSummary.heat,
    trendVelocity: sourceSummary.velocity,
    targetCold: targetColdFactor(signal, sourceRatingReference),
    localizationGap: localizationGapFactor(signal, market),
    substituteWeakness: substituteWeaknessFactor(substitute),
  };

  let weightedSum = 0;
  let weightTotal = 0;
  for (const [key, weight] of Object.entries(OPPORTUNITY_RADAR_WEIGHTS)) {
    const value = factors[key];
    if (value === null) continue;
    weightedSum += weight * value;
    weightTotal += weight;
  }

  const score = weightTotal === 0 ? null : Math.round((weightedSum / weightTotal) * 100);
  return {
    market,
    targetTier,
    score,
    factors,
    reasons: reasonsForMarket({ market, signal, substitute, sourceSummary, factors }),
  };
}

function summarizeSourceMarkets(sourceSignals, sourceMarketCount) {
  const charting = sourceSignals.filter((signal) => isRank(signal.chartRank));
  if (charting.length === 0) {
    return {
      heat: null,
      velocity: null,
      chartingMarketCount: 0,
      sourceMarketCount,
      bestRank: null,
      averageRankHeat: null,
      breadth: 0,
      persistence: null,
      newEntryCount: 0,
      risingMarketCount: 0,
    };
  }

  const rankHeatValues = charting.map((signal) => rankHeat(signal.chartRank));
  const averageRankHeat = average(rankHeatValues);
  const breadth = sourceMarketCount > 0 ? charting.length / sourceMarketCount : 0;
  const seenValues = charting
    .map((signal) => finiteNumberOrNull(signal.seenDays))
    .filter((value) => value !== null);
  const persistence = seenValues.length === 0
    ? null
    : average(seenValues.map((days) => clamp(days / OPPORTUNITY_RADAR_PARAMS.persistenceReferenceDays, 0, 1)));

  const heatParts = [
    [averageRankHeat, 0.65],
    [breadth, 0.25],
    [persistence, 0.1],
  ].filter(([value]) => value !== null);
  const heat = weightedAverage(heatParts);

  const velocity = trendVelocityFactor(charting);

  return {
    heat,
    velocity,
    chartingMarketCount: charting.length,
    sourceMarketCount,
    bestRank: Math.min(...charting.map((signal) => signal.chartRank)),
    averageRankHeat,
    breadth,
    persistence,
    newEntryCount: charting.filter((signal) => signal.isNewEntry === true || signal.previousChartRank === null).length,
    risingMarketCount: charting.filter(hasPositiveRankGain).length,
  };
}

function trendVelocityFactor(chartingSignals) {
  if (chartingSignals.length === 0) return null;

  const newEntryScore = chartingSignals.filter((signal) => signal.isNewEntry === true || signal.previousChartRank === null).length / chartingSignals.length;
  const gains = chartingSignals
    .map((signal) => rankGain(signal))
    .filter((value) => value !== null);
  const gainScore = gains.length === 0
    ? null
    : average(gains.map((gain) => clamp(gain / OPPORTUNITY_RADAR_PARAMS.velocityRankGainReference, 0, 1)));
  const simultaneousScore = chartingSignals.filter(hasPositiveRankGain).length / chartingSignals.length;

  return weightedAverage([
    [newEntryScore, 0.35],
    [gainScore, 0.45],
    [simultaneousScore, 0.2],
  ].filter(([value]) => value !== null));
}

function targetColdFactor(signal, sourceRatingReference) {
  if (!signal || signal.signalMissing === true) return null;
  if (!signal || signal.available === false) return 1;

  const rank = finiteNumberOrNull(signal.chartRank);
  const chartCold = rank === null
    ? 0.85
    : clamp((rank - OPPORTUNITY_RADAR_PARAMS.weakTargetRank) / (OPPORTUNITY_RADAR_PARAMS.chartDepth - OPPORTUNITY_RADAR_PARAMS.weakTargetRank), 0, 1);

  const ratingCount = finiteNumberOrNull(signal.ratingCount);
  const ratingReference = sourceRatingReference ?? OPPORTUNITY_RADAR_PARAMS.lowTargetRatingCount;
  const ratingCold = ratingCount === null
    ? null
    : 1 - clamp(Math.log10(ratingCount + 1) / Math.log10(Math.max(ratingReference, OPPORTUNITY_RADAR_PARAMS.lowTargetRatingCount) + 1), 0, 1);

  return weightedAverage([
    [chartCold, 0.65],
    [ratingCold, 0.35],
  ].filter(([value]) => value !== null));
}

function localizationGapFactor(signal, market) {
  if (!signal || signal.signalMissing === true) return null;
  if (!signal || signal.available === false) return null;

  const appLanguages = normalizeLanguageList(signal.languageCodesISO2A);
  if (appLanguages.length === 0) return null;

  const storefront = STOREFRONTS.find((item) => normalizeMarket(item.code) === market) ?? null;
  const localLanguages = localizationLanguagesForStorefront(storefront);
  if (localLanguages.length === 0) return null;

  const appSet = new Set(appLanguages);
  return localLanguages.some((language) => appSet.has(language)) ? 0 : 1;
}

function substituteWeaknessFactor(substitute) {
  if (!substitute || substitute.feedOk === false) return null;

  const entries = Array.isArray(substitute.entries) ? substitute.entries : [];
  if (entries.length === 0) return 1;

  const genreId = substitute.genreId == null ? null : String(substitute.genreId);
  const rankLimit = finiteNumberOrNull(substitute.rankLimit) ?? OPPORTUNITY_RADAR_PARAMS.substituteTopLimit;
  const sameGenreEntries = genreId === null
    ? entries
    : entries.filter((entry) => String(entry.genreId ?? '') === genreId);
  const topEntries = sameGenreEntries.filter((entry) => {
    const rank = finiteNumberOrNull(entry.rank);
    return rank !== null && rank <= rankLimit;
  });
  const strongEntries = topEntries.filter((entry) => {
    const ratingCount = finiteNumberOrNull(entry.ratingCount);
    const rank = finiteNumberOrNull(entry.rank);
    return ratingCount === null
      ? rank !== null && rank <= 10
      : ratingCount >= OPPORTUNITY_RADAR_PARAMS.strongSubstituteRatingCount;
  });

  const sameGenreCoverage = clamp(topEntries.length / Math.max(rankLimit, 1), 0, 1);
  const strongCoverage = clamp(strongEntries.length / OPPORTUNITY_RADAR_PARAMS.strongSubstituteReferenceCount, 0, 1);
  return clamp(1 - (sameGenreCoverage * 0.35 + strongCoverage * 0.65), 0, 1);
}

function reasonsForMarket({ market, signal, substitute, sourceSummary, factors }) {
  const reasons = [];

  if (factors.sourceHeat !== null && sourceSummary.bestRank !== null) {
    reasons.push({
      code: 'source-heat',
      signal: 'sourceHeat',
      detail: `Charts in ${sourceSummary.chartingMarketCount} source markets; best source rank #${sourceSummary.bestRank}.`,
    });
  }

  if (factors.trendVelocity !== null && (sourceSummary.newEntryCount > 0 || sourceSummary.risingMarketCount > 0)) {
    reasons.push({
      code: 'trend-velocity',
      signal: 'trendVelocity',
      detail: `${sourceSummary.newEntryCount} new source entries and ${sourceSummary.risingMarketCount} rising source markets.`,
    });
  }

  if (signal?.available === false) {
    reasons.push({
      code: 'target-unavailable',
      signal: 'targetCold',
      detail: `${market.toUpperCase()} lookup is unavailable or absent.`,
    });
  } else if (factors.targetCold !== null && factors.targetCold >= 0.7) {
    reasons.push({
      code: 'target-cold',
      signal: 'targetCold',
      detail: `${market.toUpperCase()} is weak or absent from top charts with low local rating volume.`,
    });
  }

  if (factors.localizationGap === 1) {
    reasons.push({
      code: 'localization-gap',
      signal: 'localizationGap',
      detail: `${market.toUpperCase()} storefront language is missing from Apple languageCodesISO2A.`,
    });
  }

  if (factors.substituteWeakness !== null && factors.substituteWeakness >= 0.75) {
    const checked = Array.isArray(substitute?.entries) ? substitute.entries.length : 0;
    reasons.push({
      code: 'weak-substitutes',
      signal: 'substituteWeakness',
      detail: `${market.toUpperCase()} category top ${substitute?.rankLimit ?? OPPORTUNITY_RADAR_PARAMS.substituteTopLimit} has ${checked} checked entries and few strong same-category substitutes.`,
    });
  }

  return reasons;
}

function topReasons(targetScores) {
  return [...targetScores]
    .sort((a, b) => (b.score ?? -1) - (a.score ?? -1) || a.market.localeCompare(b.market))
    .flatMap((target) => target.reasons.map((reason) => ({ ...reason, market: target.market })))
    .slice(0, 5);
}

function sourceRatingMedian(sourceSignals) {
  const values = sourceSignals
    .map((signal) => finiteNumberOrNull(signal.ratingCount))
    .filter((value) => value !== null)
    .sort((a, b) => a - b);
  if (values.length === 0) return null;
  const mid = Math.floor(values.length / 2);
  return values.length % 2 === 0 ? (values[mid - 1] + values[mid]) / 2 : values[mid];
}

function normalizeLanguageList(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value
    .map((item) => String(item ?? '').trim().toLowerCase().match(/^[a-z]{2}/)?.[0] ?? null)
    .filter(Boolean))];
}

function normalizeMarketList(markets) {
  return [...normalizeMarketSet(markets)];
}

function normalizeMarketSet(markets) {
  return new Set((Array.isArray(markets) ? markets : [])
    .map(normalizeMarket)
    .filter(Boolean));
}

function normalizeMarket(value) {
  return String(value ?? '').trim().toLowerCase();
}

function rankGain(signal) {
  const previous = finiteNumberOrNull(signal.previousChartRank);
  const current = finiteNumberOrNull(signal.chartRank);
  if (previous === null || current === null) return null;
  return Math.max(0, previous - current);
}

function hasPositiveRankGain(signal) {
  const gain = rankGain(signal);
  return gain !== null && gain > 0;
}

function rankHeat(rank) {
  return clamp(1 - (rank - 1) / (OPPORTUNITY_RADAR_PARAMS.chartDepth - 1), 0, 1);
}

function isRank(value) {
  const n = finiteNumberOrNull(value);
  return n !== null && n >= 1 && n <= OPPORTUNITY_RADAR_PARAMS.chartDepth;
}

function finiteNumberOrNull(value) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function average(values) {
  return values.reduce((sum, value) => sum + value, 0) / values.length;
}

function weightedAverage(pairs) {
  const totals = pairs.reduce((acc, [value, weight]) => {
    if (value === null || !Number.isFinite(value) || weight <= 0) return acc;
    acc.sum += value * weight;
    acc.weight += weight;
    return acc;
  }, { sum: 0, weight: 0 });
  return totals.weight === 0 ? null : clamp(totals.sum / totals.weight, 0, 1);
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
